import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Camera, Microphone, ClipboardText, Keyboard, Check, ArrowLeft } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { QuickPhotoMode } from './quickadd/QuickPhotoMode';
import { QuickVoiceMode } from './quickadd/QuickVoiceMode';
import { QuickTextMode } from './quickadd/QuickTextMode';
import { QuickRéserveMode } from './quickadd/QuickReserveMode';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function ModeSelectionGrid({ onPhoto, onVoice, onText, onRéserve, fileInputRef }) {
  return (
    <div className="grid grid-cols-2 gap-3 py-4">
      <button onClick={() => { onPhoto(); fileInputRef.current?.click(); }}
        className="flex flex-col items-center gap-3 p-5 rounded-xl bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 active:scale-95 transition-all"
        data-testid="quick-photo-btn">
        <div className="w-12 h-12 rounded-full bg-[#0055FF] flex items-center justify-center">
          <Camera size={24} className="text-white" weight="fill" />
        </div>
        <span className="font-semibold text-sm text-[#0055FF]">Photo</span>
      </button>
      <button onClick={onVoice}
        className="flex flex-col items-center gap-3 p-5 rounded-xl bg-orange-50 border-2 border-orange-200 hover:bg-orange-100 active:scale-95 transition-all"
        data-testid="quick-voice-btn">
        <div className="w-12 h-12 rounded-full bg-[#FF6B00] flex items-center justify-center">
          <Microphone size={24} className="text-white" weight="fill" />
        </div>
        <span className="font-semibold text-sm text-[#FF6B00]">Vocale</span>
      </button>
      <button onClick={onText}
        className="flex flex-col items-center gap-3 p-5 rounded-xl bg-violet-50 border-2 border-violet-200 hover:bg-violet-100 active:scale-95 transition-all"
        data-testid="quick-text-btn">
        <div className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center">
          <Keyboard size={24} className="text-white" weight="fill" />
        </div>
        <span className="font-semibold text-sm text-violet-700">Texte</span>
      </button>
      <button onClick={onRéserve}
        className="flex flex-col items-center gap-3 p-5 rounded-xl bg-emerald-50 border-2 border-emerald-200 hover:bg-emerald-100 active:scale-95 transition-all"
        data-testid="quick-reserve-btn">
        <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center">
          <ClipboardText size={24} className="text-white" weight="fill" />
        </div>
        <span className="font-semibold text-sm text-emerald-700">Reserve</span>
      </button>
    </div>
  );
}

function SuccessFeedback({ message }) {
  return (
    <div className="py-8 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
        <Check size={32} className="text-emerald-600" weight="bold" />
      </div>
      <p className="text-base font-semibold text-emerald-700">{message || 'Enregistre !'}</p>
    </div>
  );
}

export function QuickAddFAB({ chantiers = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [quickRéserve, setQuickRéserve] = useState({
    chantier_id: '', title: '', location: '', priority: 'medium'
  });
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const navigate = useNavigate();

  const resetState = () => {
    setActiveMode(null); setIsRecording(false); setTranscription('');
    setIsProcessing(false); setIsSaving(false); setCapturedPhoto(null);
    setSuccess(false); setSuccessMsg('');
    setQuickRéserve({ chantier_id: '', title: '', location: '', priority: 'medium' });
  };

  const handleClose = () => { resetState(); setIsOpen(false); };

  const showSuccess = (msg) => {
    setSuccess(true);
    setSuccessMsg(msg);
    setTimeout(() => handleClose(), 1500);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedPhoto(file);
    setActiveMode('photo-confirm');
  };

  const savePhoto = async () => {
    if (!capturedPhoto || !quickRéserve.chantier_id) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedPhoto);
      await axios.post(`${API}/upload?chantier_id=${quickRéserve.chantier_id}`, formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      showSuccess('Photo enregistree !');
    } catch (_err) { toast.error('Erreur upload photo'); }
    finally { setIsProcessing(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => { audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        setIsProcessing(true);
        const formData = new FormData();
        formData.append('file', audioBlob, 'voice_note.webm');
        if (quickRéserve.chantier_id) formData.append('chantier_id', quickRéserve.chantier_id);
        try {
          const response = await axios.post(`${API}/voice-notes/transcribe`, formData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
          setTranscription(response.data.transcription || '(Aucun texte détecté)');
        } catch (_err) { toast.error('Erreur transcription audio. Reessayez ou saisissez au clavier.'); }
        finally { setIsProcessing(false); }
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (_err) { toast.error('Erreur acces microphone'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Save transcription to chosen destination
  const saveToDestination = async (destination) => {
    if (!transcription) return;
    setIsSaving(true);
    const cid = quickRéserve.chantier_id;
    try {
      switch (destination) {
        case 'journal':
          await axios.post(`${API}/notes`, {
            text: transcription, chantier_id: cid || null,
          }, { withCredentials: true });
          showSuccess('Ajouté au journal !');
          break;

        case 'reserve':
          if (!cid) { toast.error('Sélectionnez un chantier pour créer une réserve'); setIsSaving(false); return; }
          await axios.post(`${API}/reserves`, {
            chantier_id: cid,
            title: transcription.slice(0, 80),
            description: transcription,
            location: '',
            priority: 'medium',
          }, { withCredentials: true });
          showSuccess('Réserve créée !');
          break;

        case 'observation':
          if (!cid) { toast.error('Sélectionnez un chantier pour créer une observation'); setIsSaving(false); return; }
          await axios.post(`${API}/observations`, {
            chantier_id: cid,
            type: 'general',
            title: transcription.slice(0, 80),
            description: transcription,
            severity: 'medium',
          }, { withCredentials: true });
          showSuccess('Observation créée !');
          break;

        case 'compte_rendu':
          await axios.post(`${API}/notes`, {
            text: `[Compte-rendu] ${transcription}`,
            chantier_id: cid || null,
          }, { withCredentials: true });
          showSuccess('Ajouté au compte-rendu !');
          break;

        default:
          break;
      }
    } catch (_err) {
      toast.error('Erreur enregistrement');
    }
    finally { setIsSaving(false); }
  };

  const createQuickRéserve = async () => {
    if (!quickRéserve.chantier_id || !quickRéserve.title) return;
    setIsProcessing(true);
    try {
      await axios.post(`${API}/reserves`, {
        ...quickRéserve,
        description: `Réserve rapide créée sur le terrain${transcription ? `\n\nNote vocale: ${transcription}` : ''}`
      }, { withCredentials: true });
      showSuccess('Reserve creee !');
    } catch (_err) { toast.error('Erreur création réserve rapide'); }
    finally { setIsProcessing(false); }
  };

  const saveTextNote = async (text) => {
    if (!text.trim()) return;
    const targetChantierId = quickRéserve.chantier_id;
    if (!targetChantierId) {
      toast.error('Sélectionnez un chantier avant d\'enregistrer');
      return;
    }
    setIsProcessing(true);
    try {
      await axios.post(`${API}/notes`, {
        text: text.trim(),
        chantier_id: targetChantierId,
      }, { withCredentials: true });
      toast.success('Note enregistrée dans le journal du chantier', {
        action: {
          label: 'Voir',
          onClick: () => { window.location.href = `/journal?chantier=${targetChantierId}`; },
        },
      });
      setIsOpen(false);
      setActiveMode(null);
    } catch (_err) { toast.error('Erreur enregistrement note'); }
    finally { setIsProcessing(false); }
  };

  return (
    <>
      <button onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FF8C00] text-white shadow-xl hover:shadow-2xl flex items-center justify-center active:scale-95 transition-all duration-150"
        data-testid="quick-add-fab"
        style={{ boxShadow: '0 4px 20px rgba(255, 107, 0, 0.4)' }}>
        <Plus size={32} weight="bold" />
      </button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl fab-sheet" aria-describedby="quick-add-desc">
          <div className="fab-sheet-scroll">
            <SheetHeader className="pb-2 flex-shrink-0">
              <SheetTitle className="text-lg flex items-center gap-2">
                {activeMode && (
                  <button onClick={() => { setActiveMode(null); setCapturedPhoto(null); setTranscription(''); setIsRecording(false); }}
                    className="p-1.5 -ml-1 rounded-lg hover:bg-gray-100 active:scale-95 transition"
                    data-testid="quick-add-back-btn" aria-label="Retour au menu">
                    <ArrowLeft size={20} className="text-[#4B5563]" />
                  </button>
                )}
                {!activeMode && <><Plus size={20} className="text-[#FF6B00]" /> Ajout rapide</>}
                {activeMode === 'photo' && 'Prendre une photo'}
                {activeMode === 'photo-confirm' && 'Confirmer la photo'}
                {activeMode === 'voice' && 'Note vocale'}
                {activeMode === 'text' && 'Note écrite'}
                {activeMode === 'réserve' && 'Réserve rapide'}
              </SheetTitle>
              <p id="quick-add-desc" className="text-xs text-[#4B5563]">
                {!activeMode && 'Action rapide sur chantier'}
                {activeMode === 'voice' && 'Dictez votre observation'}
                {activeMode === 'text' && 'Saisissez votre observation au clavier'}
              </p>
            </SheetHeader>

            {success && <SuccessFeedback message={successMsg} />}

            {!activeMode && !success && (
              <ModeSelectionGrid
                onPhoto={() => setActiveMode('photo')}
                onVoice={() => setActiveMode('voice')}
                onText={() => setActiveMode('text')}
                onRéserve={() => setActiveMode('réserve')}
                fileInputRef={fileInputRef} />
            )}

            {activeMode === 'photo-confirm' && !success && (
              <QuickPhotoMode capturedPhoto={capturedPhoto}
                chantierId={quickRéserve.chantier_id}
                onChantierChange={(value) => setQuickRéserve({...quickRéserve, chantier_id: value})}
                chantiers={chantiers} isProcessing={isProcessing}
                onSave={savePhoto} onCancel={() => setActiveMode(null)} />
            )}

            {activeMode === 'voice' && !success && (
              <QuickVoiceMode chantierId={quickRéserve.chantier_id}
                onChantierChange={(value) => setQuickRéserve({...quickRéserve, chantier_id: value})}
                chantiers={chantiers} isRecording={isRecording}
                isProcessing={isProcessing} transcription={transcription}
                onStartRecording={startRecording} onStopRecording={stopRecording}
                onCancel={() => setActiveMode(null)}
                onSwitchToText={() => setActiveMode('text')}
                onSaveToDestination={saveToDestination}
                isSaving={isSaving} />
            )}

            {activeMode === 'text' && !success && (
              <QuickTextMode chantierId={quickRéserve.chantier_id}
                onChantierChange={(value) => setQuickRéserve({...quickRéserve, chantier_id: value})}
                chantiers={chantiers} isProcessing={isProcessing}
                onSave={saveTextNote}
                onCancel={() => setActiveMode(null)}
                onSwitchToVoice={() => setActiveMode('voice')} />
            )}

            {activeMode === 'réserve' && !success && (
              <QuickRéserveMode quickRéserve={quickRéserve}
                setQuickRéserve={setQuickRéserve} chantiers={chantiers}
                isProcessing={isProcessing} onSave={createQuickRéserve}
                onCancel={() => setActiveMode(null)} />
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
            onChange={handlePhotoCapture} className="hidden" />
        </SheetContent>
      </Sheet>

      <style>{`
        .fab-sheet {
          max-height: 85vh !important;
          max-height: 85dvh !important;
          height: auto !important;
          display: flex !important;
          flex-direction: column !important;
          padding-bottom: max(1rem, env(safe-area-inset-bottom)) !important;
        }
        .fab-sheet-scroll {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 0 1.5rem 0.5rem;
          min-height: 0;
        }
      `}</style>
    </>
  );
}
